import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Flex,
    Stack,
    useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData } from '../../util/helper';
import { createNewStaticWord, getStaticWordData, getStaticWordByKey, updateStaticWordData } from '../../services/StaticWords';
import { handleApiError, showErrorToast, showSuccessToast } from '../../util/errorHandler';

const StaticWord = () => {
    const { id } = useParams(); // This is the 'key' from the URL
    const [processedData, setProcessedData] = useState({});
    const [recordId, setRecordId] = useState(null); // Store the actual _id for API calls
    const [quickSave, setQuickSave] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const schema = {
        "title": "Static Word Fields",
        "description": "",
        "type": "object",
        "properties": {
            "key": {
                "type": "string",
                "title": "Key",
                "default": "",
                "description": "The translation key identifier"
            },
            "languages": {
                "type": "array",
                "title": "Languages",
                "description": "Translations for different languages",
                "items": {
                    "type": "object",
                    "properties": {
                        "lang_code": {
                            "type": "string",
                            "title": "Language Code",
                            "description" : "Language code (en or hin)",
                            "enum" : ["en", "hin"],
                            "default": "en"
                        },
                        "value": {
                            "type": "string",
                            "title": "Translation Value",
                            "description": "The translated text"
                        }
                    },
                    "required": ["lang_code", "value"]
                },
                "default": []
            }
        },
        required: ['key', 'languages']
    };

    const uiSchema = {
        "ui:submitButtonOptions": {
            "submitText": "Save",
            "norender": false,
            "props": {
                "disabled": false,
                "className": "button button--primary button--blue",
                "loadingText": "Saving..",
                "colorScheme": "teal"
            }
        },
        key: {
            'ui:emptyValue': '',
            'ui:readonly': !!id
        },
        languages: {
            'ui:options': {
                orderable: true,
                addable: true,
                removable: true
            },
            items: {
                'ui:ObjectFieldTemplate': (props) => {
                    return (
                        <Flex gap={4} align="flex-start" width="100%">
                            {props.properties.map((element, index) => (
                                <Box 
                                    key={index} 
                                    flex={element?.name === 'lang_code' ? '0 0 200px' : '1'}
                                    minWidth={element?.name === 'lang_code' ? '200px' : '0'}
                                >
                                    {element.content}
                                </Box>
                            ))}
                        </Flex>
                    );
                },
                lang_code: {
                    'ui:options': {
                        enumOptions: [
                            { value: 'en', label: 'en' },
                            { value: 'hin', label: 'hin' }
                        ]
                    }
                },
                value: {
                    'ui:emptyValue': '',
                    'ui:widget': 'textarea',
                    'ui:options': {
                        rows: 1,
                        width: "100%"
                    }
                }
            }
        }
    };

    const formData = processedData;

    // Fetch initial data
    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (id) {
                    // First, try to get the record by key to find its _id
                    const record = await getStaticWordByKey(id);
                    
                    if (record && record?._id) {
                        // Now fetch the full data using the _id
                        const result = await getStaticWordData(record._id);
                        
                        if (result) {
                            // The API returns an array, so we need to get the first element
                            const data = Array.isArray(result) ? result[0] : result;
                            
                            if (data && data?.key) {
                                // Store the _id for API calls
                                setRecordId(data?._id);
                                setProcessedData(data);
                            }
                        }
                    }
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch data"
                });
            }
        };

        getBasicData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, quickSave]);

    // Submit updated data
    const handleSubmit = async (formData) => {
        // Check for duplicate language codes
        if (formData?.languages && formData?.languages?.length > 0) {
            const langCodes = formData?.languages?.map(lang => lang?.lang_code).filter(Boolean);
            const duplicates = [...new Set(langCodes.filter((code, index) => langCodes.indexOf(code) !== index))];
            
            if (duplicates.length > 0) {
                showErrorToast(toast, `Each language code must be unique. Duplicate found: ${duplicates.join(', ')}`);
                return; // Prevent form submission
            }
        }

        // Clean the data - remove _id fields from languages array and metadata
        const cleanedData = {
            key: formData?.key,
            languages: formData?.languages?.map(lang => ({
                lang_code: lang?.lang_code,
                value: lang?.value
            })) || []
        };

        const updatedFormData = reverseTransformData(cleanedData, []);

        try {
            if (recordId) {
                // Use the stored _id for update
                const result = await updateStaticWordData(recordId, updatedFormData);

                if (result?.status === 200) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
                }
            } else {
                const result = await createNewStaticWord(updatedFormData);  // Call the create API
                if (result?.status === 201) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "New static word created successfully");
                    // Navigate using the key from the form data
                    navigate(`/static-words/${formData.key}/general/basic`);
                }
            }
        } catch (error) {
            handleApiError(error, toast);
        }
    };

    // Custom template for description fields
    const templates = {
        DescriptionFieldTemplate: (props) => {
            if (!props.description) return null;
            return (
                <Box 
                    as="p" 
                    fontSize="12px" 
                    fontStyle="italic" 
                    color="gray.600" 
                    mt={1}
                    mb={2}
                >
                    {props.description}
                </Box>
            );
        }
    };

    return (
        <Stack flexDirection={'row'} gap={"15px"} alignItems={"flex-start"}>
            <Box width={"900px"} className="static-words-form-container">
                <style>
                    {`
                        /* Scoped styles - only apply to Static Words form */
                        /* Target all description paragraphs in the form */
                        .static-words-form-container .field-description,
                        .static-words-form-container .chakra-form__helper-text,
                        .static-words-form-container p:not(.chakra-text),
                        .static-words-form-container .form-group > p,
                        .static-words-form-container fieldset > p,
                        .static-words-form-container .field > p,
                        .static-words-form-container div[class*="field"] > p {
                            font-size: 12px !important;
                            font-style: italic !important;
                            color: #718096 !important;
                            margin-top: 4px !important;
                        }
                    `}
                </style>
                <Form
                    schema={schema}
                    uiSchema={uiSchema}
                    formData={formData}
                    validator={validator}
                    templates={templates}
                    onSubmit={(e) => handleSubmit(e.formData)}
                />
            </Box>
        </Stack>
    );
};

export default StaticWord;
